// src/pages/nfl/PlayerPool.jsx
import React, { useEffect, useMemo, useState } from "react";

/* ------------------------------- data urls ------------------------------ */
const POOL_URL = "/data/nfl/classic/latest/player_pool.json";
const META_URL = "/data/nfl/classic/latest/meta.json";

/* -------------------------------- utils --------------------------------- */
const teamLogo = (abv) =>
  `/logos/nfl/${String(abv || "").toUpperCase()}.png`;

const num = (v) => {
  const n = Number(String(v ?? "").replace(/[,$%\s]/gi, ""));
  return Number.isFinite(n) ? n : null;
};
const fmt0 = (v) => {
  const n = num(v);
  return n == null
    ? "—"
    : n.toLocaleString(undefined, { maximumFractionDigits: 0 });
};
const fmt1 = (v) => {
  const n = num(v);
  return n == null ? "—" : n.toFixed(1);
};
const fmtPct1 = (v) => {
  if (v == null || v === "") return "—";
  let s = String(v).trim();
  if (s.endsWith("%")) {
    const n = num(s.slice(0, -1));
    return n == null ? "—" : `${n.toFixed(1)}%`;
  }
  let n = num(s);
  if (n == null) return "—";
  if (Math.abs(n) <= 1) n *= 100;
  return `${n.toFixed(1)}%`;
};
const time12 = (v) => {
  if (!v) return "—";
  const s = String(v).trim();
  if (/\d{1,2}:\d{2}(:\d{2})?\s?[AP]M/i.test(s)) return s.toUpperCase();
  const m = s.match(/^(\d{1,2})\s*:\s*(\d{2})\s*([AP])?M?$/i);
  if (!m) return s;
  let hh = Number(m[1]),
    mm = m[2],
    ampm = (m[3] || "A").toUpperCase() === "A" ? "AM" : "PM";
  if (hh === 0) hh = 12;
  else if (hh === 12) ampm = "PM";
  else if (hh > 12) {
    hh -= 12;
    ampm = "PM";
  }
  return `${hh}:${mm} ${ampm}`;
};

function useJson(url) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setLoading(true);
        setErr("");
        const r = await fetch(`${url}?_=${Date.now()}`, {
          cache: "no-store",
        });
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const j = await r.json();
        if (alive) setData(j);
      } catch (e) {
        if (alive) setErr(String(e.message || e));
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [url]);
  return { data, err, loading };
}

/* ------------------------------- tiny UI -------------------------------- */

const thBase =
  "px-1 py-2 text-center font-semibold text-[10px] uppercase leading-tight [overflow-wrap:anywhere] first:w-[26%]";
const tdBase =
  "px-1 py-2 text-center text-[11px] leading-tight [overflow-wrap:anywhere] align-middle";

// table columns
const COLS = [
  "Player",
  "Salary",
  "Team",
  "Opp",
  "O/U",
  "Imp. Total",
  "Time",
  "Proj",
  "Value",
  "pOWN",
  "Tag",
];

// responsive visibility per column
// Only hide some of the Vegas stuff on tiny screens;
// keep Value / pOWN / Cash-GPP visible everywhere.
const COL_VIS = {
  "O/U": "hidden md:table-cell",
  "Imp. Total": "hidden sm:table-cell",
  // Value: "",          // always visible
  // pOWN: "",           // always visible
  // "Cash/GPP/Both": "",// always visible
};

function HeadRow({ cols, isDark }) {
  // dark: slate header (so blue isn't doubled)
  // light: yellow header (like before)
  const headBg = isDark
    ? "bg-slate-900/95 text-slate-100"
    : "bg-amber-400 text-slate-800";

  const borderClass = isDark ? "border-slate-800" : "border-amber-500";

  return (
    <thead className={`${headBg} sticky top-0 z-10`}>
      <tr>
        {cols.map((c) => (
          <th
            key={c}
            className={`${thBase} border-b ${borderClass} ${
              COL_VIS[c] || ""
            }`}
          >
            {c}
          </th>
        ))}
      </tr>
    </thead>
  );
}

function TeamCell({ team }) {
  const abv = String(team || "").toUpperCase();
  return (
    <div className="flex shrink-0 items-center gap-1">
      <img
        src={teamLogo(abv)}
        alt=""
        className="w-4 h-4 shrink-0 object-contain opacity-90"
        onError={(e) => (e.currentTarget.style.visibility = "hidden")}
      />
      <span className="font-medium text-[11px]">{abv}</span>
    </div>
  );
}

const NAME_KEYS = ["Player", "QB", "RB", "WR", "TE", "DST", "Cash Core"];
const NUMERICISH_KEYS = new Set([
  "Salary",
  "Team",
  "Opp",
  "O/U",
  "Imp. Total",
  "Imp Total",
  "Time",
  "Proj",
  "Projection",
  "Value",
  "pOWN",
]);

function getDisplayName(row) {
  if (!row) return "—";
  for (const k of NAME_KEYS) {
    const v = row[k];
    if (v && String(v).trim()) return String(v).trim();
  }
  for (const [k, v] of Object.entries(row)) {
    if (!NUMERICISH_KEYS.has(k) && typeof v === "string" && v.trim())
      return v.trim();
  }
  return "—";
}

function Block({ title, children }) {
  const cardClass =
    "rounded-2xl border border-slate-800 bg-slate-950 shadow-[0_16px_40px_rgba(15,23,42,0.9)] overflow-hidden dark:border-slate-800 dark:bg-slate-950";

  // Section titles (QB/RB/WR/TE/DST/Cash Core) always blue
  const headerClass = "bg-sky-700 text-white border-b border-sky-800";

  return (
    <div className={cardClass + " w-full min-w-0"}>
      <div className={"px-3 py-2 text-sm font-semibold " + headerClass}>
        {title}
      </div>
      {children}
    </div>
  );
}

/* -------------------------------- page ---------------------------------- */

const SECTIONS_LEFT = ["QB", "RB", "WR"];
const SECTIONS_RIGHT = ["TE", "DST", "Cash Core"];

export default function PlayerPool() {
  const { data: pool, err, loading } = useJson(POOL_URL);
  const { data: meta } = useJson(META_URL);

  const [theme, setTheme] = useState("dark");
  const isDark = theme === "dark";

  const byLabel = (label) => {
    const tbl = pool?.tables?.find(
      (t) => (t?.label || "").trim().toLowerCase() === label.toLowerCase()
    );
    return Array.isArray(tbl?.rows) ? tbl.rows : [];
  };

  const updatedStamp = useMemo(() => {
    if (!meta) return "";
    const art = meta?.artifacts?.find((x) =>
      String(x.path || "").endsWith(POOL_URL)
    );
    const raw =
      art?.last_updated || art?.tags?.last_updated || meta?.last_updated;
    if (!raw) return "";
    const d = new Date(raw);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleString(undefined, {
      month: "numeric",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  }, [meta]);

  const rowOdd = isDark ? "bg-slate-950" : "bg-white";
  const rowEven = isDark ? "bg-slate-900/85" : "bg-gray-50";

  const renderRow = (r) => {
    // more forgiving mapping for projections / value / ownership
    const projRaw =
      r.Proj ??
      r["Projection"] ??
      r["DK Proj"] ??
      r["Proj DK"] ??
      r["DK Projection"];
    const valRaw = r.Value ?? r["DK Val"] ?? r["Value DK"];
    const ownRaw =
      r.pOWN ??
      r["pOWN%"] ??
      r["DK pOWN%"] ??
      r["OWN"] ??
      r["Ownership"];

    return (
      <>
        {/* Player */}
        <td className={`${tdBase} text-left`}>
          <div className="flex min-w-0 flex-wrap items-center gap-x-1.5 gap-y-0.5">
            <TeamCell team={r.Team} />
            <div className="min-w-0 [overflow-wrap:anywhere]">{getDisplayName(r)}</div>
          </div>
        </td>

        {/* Salary */}
        <td className={`${tdBase} tabular-nums`}>{fmt0(r.Salary)}</td>

        {/* Team / Opp */}
        <td className={tdBase}>{String(r.Team || "").toUpperCase()}</td>
        <td className={tdBase}>{String(r.Opp || "").toUpperCase()}</td>

        {/* O/U */}
        <td className={`${tdBase} ${COL_VIS["O/U"]}`}>{fmt1(r["O/U"])}</td>

        {/* Imp Total */}
        <td className={`${tdBase} ${COL_VIS["Imp. Total"]}`}>
          {fmt1(r["Imp. Total"] ?? r["Imp Total"])}
        </td>

        {/* Time */}
        <td className={tdBase}>{time12(r.Time)}</td>

        {/* Proj */}
        <td className={`${tdBase} tabular-nums`}>{fmt1(projRaw)}</td>

        {/* Value */}
        <td className={`${tdBase}`}>{fmt1(valRaw)}</td>

        {/* pOWN */}
        <td className={`${tdBase}`}>{fmtPct1(ownRaw)}</td>

        {/* Cash/GPP/Both */}
        <td className={`${tdBase}`}>
          {r["Cash/GPP/Both"] || r.Role || "—"}
        </td>
      </>
    );
  };

  const outerClass = isDark
    ? "min-h-screen bg-slate-950 text-slate-100"
    : "min-h-screen bg-slate-100 text-slate-900";

  return (
    <div className={outerClass}>
      {/* wider page */}
      <div className="mx-auto max-w-[1800px] px-3 sm:px-5 lg:px-6 py-5 md:py-6">
        {/* header bar */}
        <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex min-w-0 flex-wrap items-baseline gap-x-3 gap-y-1">
            <h1 className="text-2xl font-extrabold tracking-tight md:text-3xl">
              NFL — Player Pool
            </h1>
            <span
              className={"text-xs italic " + (isDark ? "text-slate-400" : "text-slate-500")}
            >
              Updated: {updatedStamp || "—"}
            </span>
          </div>

          {/* Theme toggle */}
          <div className="flex items-center gap-2 self-start sm:self-auto">
            <span className="text-xs text-slate-400">Theme</span>
            <div className="inline-flex rounded-full bg-slate-900/70 p-1 border border-slate-700/70">
              {["dark", "light"].map((t) => {
                const active = theme === t;
                return (
                  <button
                    key={t}
                    onClick={() => setTheme(t)}
                    className={
                      "px-2.5 py-1 rounded-full text-xs transition " +
                      (active
                        ? "bg-sky-500 text-slate-950 shadow"
                        : "text-slate-300 hover:text-white")
                    }
                  >
                    {t === "dark" ? "Dark" : "Light"}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {loading ? (
          <div className="text-slate-400">Loading…</div>
        ) : err ? (
          <div className="text-red-400 font-semibold">Error: {err}</div>
        ) : (
          <div className="grid grid-cols-1 xl:grid-cols-2 items-start gap-4 lg:gap-5">
            {/* LEFT SIDE */}
            <div className="flex min-w-0 flex-col gap-4 lg:gap-5">
              {SECTIONS_LEFT.map((sec) => (
                <Block key={sec} title={sec}>
                  <div className="w-full min-w-0">
                    <table
                      className={`w-full table-fixed border-separate text-sm ${
                        isDark ? "text-slate-100" : "text-slate-900"
                      }`}
                      style={{ borderSpacing: 0 }}
                    >
                      <HeadRow cols={COLS} isDark={isDark} />
                      <tbody>
                        {byLabel(sec).map((r, i) => (
                          <tr
                            key={`${sec}-${i}`}
                            className={i % 2 === 0 ? rowOdd : rowEven}
                          >
                            {renderRow(r)}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </Block>
              ))}
            </div>

            {/* RIGHT SIDE */}
            <div className="flex min-w-0 flex-col gap-4 lg:gap-5">
              {SECTIONS_RIGHT.map((sec) => (
                <Block key={sec} title={sec}>
                  <div className="w-full min-w-0">
                    <table
                      className={`w-full table-fixed border-separate text-sm ${
                        isDark ? "text-slate-100" : "text-slate-900"
                      }`}
                      style={{ borderSpacing: 0 }}
                    >
                      <HeadRow cols={COLS} isDark={isDark} />
                      <tbody>
                        {byLabel(sec).map((r, i) => (
                          <tr
                            key={`${sec}-${i}`}
                            className={i % 2 === 0 ? rowOdd : rowEven}
                          >
                            {renderRow(r)}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </Block>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
